import cn from 'classnames';
import { ComponentType, KeyboardEvent, ChangeEvent, useState } from 'react';
import { Button } from 'components/Button';
import { rm } from 'utils';
import { AddressInfo } from 'models/AddressInfo';
import { IconCheckmark } from 'components/Shared/Icon';
import { IconCross } from 'components/Shared/Icon/themes/BGH-RD3/iconCross';
import Modal from 'components/Shared/Modal';
import { CustomerType } from 'models/CustomerType';
import { CheckoutAddressAutoParams } from 'views/CheckoutPage/actions';
import { ToggleQuickstart } from '../toggleQuickstart';
import ContactInfoPolicy from 'views/CheckoutPage/PolicyInformation/themes/BGH-RD3/contactInfoPolicy';
import styles from './styles.scss';
import autoFormStyles from 'views/CheckoutPage/ContactInfo/themes/BGH-RD3/AutoForm/autoForm.scss';
import { useSelector } from 'react-redux';
import { getCustomerType, getShouldUseRotFeatureToggle } from 'views/CheckoutPage/selector';
import DeliveryForm from '../deliveryForm';
import { selectChannel } from '../selector';

export interface AddressFormProps {
  resetForm: () => void;
}

export interface AutoFormOwnProps extends AddressFormProps {
  toggleQuickStart: () => void;
}

export interface AutoFormMergedProps extends AutoFormOwnProps {
  customerType: CustomerType;
  isValid: boolean;
  deliveryAddressChanged: () => void;
  showAddressForm: boolean;
  hasDeliveryAddress: boolean;
  reset: () => void;
  loadDetails: (args?: CheckoutAddressAutoParams) => Promise<AddressInfo[]>;
  setAddressDetails: (args: AddressInfo) => void;
}

interface AddressValues {
  identityNumber?: string;
  firstName: string;
  lastName?: string;
  street: string;
  city: string;
  zipCode: string;
}

interface RenderAddressProps extends AddressValues {
  className?: string;
  onClick?: () => void;
  reset?: () => void;
}

export const AddressField = ({
  identityNumber,
  firstName,
  lastName,
  street,
  city,
  zipCode,
  className,
  onClick,
  reset
}: RenderAddressProps) => (
  <div
    className={cn(styles.address, className, { [styles.clickable]: !!onClick })}
    onClick={onClick}
  >
    <div>
      {identityNumber && <div className={styles.identityNumber}>{identityNumber}</div>}
      <div>{`${firstName} ${lastName || ''}`}</div>
      <div>{street}</div>
      <div>{`${zipCode} ${city}`}</div>
    </div>
    {reset && (
      <div className={styles.resetWrapper}>
        <IconCheckmark size="small" className={styles.icon} />
        <Button onClick={reset} appearance="link" type="button" className={styles.resetQuickstart}>
          {rm('checkout_reset_quickstart')}
        </Button>
      </div>
    )}
  </div>
);

export interface QuickStartFormProps {
  onChange: (_: ChangeEvent, n: string, p: string) => void;
  inputHandler: (e: KeyboardEvent) => void;
  loading: boolean;
  handleSubmit: () => Promise<void>;
  submitDisabled: boolean;
  noAddressError?: string;
  toggleQuickStart: () => void;
}

export default (
  AddressForm: ComponentType<AddressFormProps>,
  QuickForm: ComponentType<QuickStartFormProps>
) => {
  const AutoForm = ({
    toggleQuickStart,
    showAddressForm,
    hasDeliveryAddress,
    isValid,
    setAddressDetails,
    loadDetails,
    resetForm
  }: AutoFormMergedProps) => {
    const channel = useSelector(selectChannel);
    const [noAddressError, setNoAddressError] = useState<string>();
    const [addresses, setAddresses] = useState<AddressInfo[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const customerType = useSelector(getCustomerType);
    const shouldDisableCardPaymentROTFeatureToggle = useSelector(getShouldUseRotFeatureToggle);

    const closeAddressModal = () => {
      setAddresses([]);
      setShowModal(false);
    };
    const onChange = (_: ChangeEvent, n: string, p: string) =>
      noAddressError && n !== p && setNoAddressError(undefined);

    const inputHandler = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (isValid) {
          void handleSubmit();
          e.currentTarget.blur();
        }
      }
    };

    const handleSubmit = () => {
      setLoading(true);

      return loadDetails()
        .then(addresses => {
          const success = addresses.length > 0;
          if (!success) {
            setNoAddressError(rm('checkout_address_choice_address_none'));
          }

          if (addresses.length === 1) {
            const [address] = addresses;
            setAddressDetails(address);
          } else {
            setAddresses(addresses);
            setShowModal(true);
          }
          setLoading(false);
        })
        .catch(reason => {
          setLoading(false);
          throw reason;
        });
    };

    const showMultipleModal = showModal && addresses.length > 0;
    const multipleAddressOnChange = (a: AddressInfo) => () => {
      closeAddressModal();
      setAddressDetails(a);
    };

    const shouldShowQuickstartToggle = customerType !== CustomerType.Company || channel === 'BNO';

    return (
      <>
        {showMultipleModal && (
          <MultipleAddressesModal
            showModal={showModal}
            addresses={addresses}
            onChange={multipleAddressOnChange}
            onClose={closeAddressModal}
          />
        )}
        <div
          className={cn({
            [autoFormStyles.autoForm]: !showAddressForm
          })}
        >
          <div className={autoFormStyles.info}>
            {rm('checkout_contact_info_address_automatic_info')}
          </div>
          <div className={autoFormStyles.quickForm}>
            <QuickForm
              onChange={onChange}
              inputHandler={inputHandler}
              handleSubmit={handleSubmit}
              loading={loading}
              submitDisabled={!isValid}
              noAddressError={noAddressError}
              toggleQuickStart={toggleQuickStart}
            />
          </div>

          {showAddressForm ? (
            <AddressForm resetForm={resetForm} />
          ) : (
            <>
              {shouldShowQuickstartToggle && (
                <div className={autoFormStyles.toggle}>
                  <ToggleQuickstart
                    label={
                      shouldDisableCardPaymentROTFeatureToggle
                        ? rm('checkout_contact_info_address_ROT_Feature')
                        : rm('checkout_contact_info_address_manual_without_ssn')
                    }
                    onClick={toggleQuickStart}
                    shouldDisableCardPaymentROTFeatureToggle={
                      shouldDisableCardPaymentROTFeatureToggle
                    }
                  />
                </div>
              )}
              <div className={autoFormStyles.integrity}>
                <ContactInfoPolicy />
              </div>
            </>
          )}
        </div>
        {hasDeliveryAddress && <DeliveryForm customerType={customerType} />}
      </>
    );
  };

  return AutoForm;
};

interface MultipleAddressesModal {
  showModal: boolean;
  addresses: AddressInfo[];
  onChange: (a: AddressInfo) => () => void;
  onClose: () => void;
}

const MultipleAddressesModal = ({
  showModal,
  addresses,
  onChange,
  onClose
}: MultipleAddressesModal) => (
  <Modal show={showModal} contentClassName={styles.multipleAddressesModal}>
    <div className={styles.multipleAddressesModalTitle}>
      <h2>{rm('checkout_address_choose_headline')}</h2>
      <div className={styles.multipleAddressesModalCloseButton} onClick={onClose}>
        <IconCross bold />
      </div>
    </div>
    <span>{rm('checkout_address_choose_body_rd3')}</span>
    <div className={styles.addresses}>
      {addresses.map((a: AddressInfo) => (
        <AddressField
          className={styles.addressField}
          key={a.addressSelector}
          onClick={onChange(a)}
          firstName={a.firstName}
          lastName={a.lastName}
          street={a.street}
          city={a.city}
          zipCode={a.zipCode}
        />
      ))}
    </div>
  </Modal>
);
